/**
 * signLink.ts — the client for `sign-agreement-public`, the endpoint behind every
 * signing LINK.
 *
 * ── ONE TRANSPORT, TWO KINDS OF SIGNER ────────────────────────────────────────
 * A link is what Primelane shares with somebody who has no account and cannot be given
 * one. Two parties are in that position, and the endpoint serves both through the same
 * three actions:
 *
 *   WITNESS  signs the WITNESS AGREEMENT — their own instrument, which the hirer never
 *            signs; the hire purchase agreement seats the driver and Primelane alone. It is
 *            frozen before the link exists, because `agreement_issue_token`'s WITNESS arm
 *            freezes it against the hash the admin reviewed, so `content_sha256` is the hash
 *            taken when it froze and the document row is where the broker reads it.
 *   DRIVER   the payer of a deposit, weeks before a vehicle, a contract or an account
 *            exists. They sign FIRST, so nothing has frozen the text: SQL renders it LIVE
 *            and `content_sha256` is the hash of exactly the bytes it just served, which
 *            is what their own signature then seals the document against.
 *
 * Either way the hash arrives alongside the text and is NOT echoed unexamined — the page
 * digests what it displayed and refuses to sign on a disagreement. See `hashAttestation`
 * in ./sign-link-ceremony.ts, which is where that rule lives because it is a decision and
 * not a transport concern.
 *
 * ── WHY PLAIN FETCH, AND NOT THE SUPABASE CLIENT ──────────────────────────────
 * A link-holder has no account. The function is deployed with `verify_jwt = false`
 * precisely so their link works without one, and `supabase.functions.invoke` attaches
 * whatever session happens to be in localStorage — which on a shared phone could be the
 * DRIVER's session, silently changing who the edge function thinks is calling. So the
 * requests go out with the publishable key and nothing else: no session, no user JWT, no
 * `Authorization` header carrying somebody else's identity.
 *
 * THE TOKEN IS THE CREDENTIAL. It arrives in `location.hash`, which browsers never put on
 * the wire, and it is sent only in the POST body — never in a query string, never into
 * localStorage. It is NOT, however, invisible by itself: anything that reads
 * `location.href` sees it, which is what error reporting does. Two things therefore keep it
 * out of telemetry, and both have to hold:
 *
 *   1. `SignLink` lifts the token into memory on its first render and immediately
 *      `history.replaceState`s the fragment away, so the URL stops carrying it.
 *   2. `src/sentry.ts` truncates every outgoing URL — event `request.url`, transaction
 *      names, breadcrumb urls — at the first `#`, so an event captured before step 1 ran,
 *      or by a page that never runs it, still cannot carry a fragment off the device.
 *
 * ── THE ERROR CONTRACT ────────────────────────────────────────────────────────
 * Every rejection is the same sentence with the same status, so the endpoint cannot be
 * used to work out which tokens exist. THREE codes are the exception and arrive as
 * `{ error, code }`: they are raised only after the token has been accepted, so a caller
 * who sees one has already proven they hold the link. `SignLinkError` carries the code
 * through to the UI, which is what lets a stale tab be told to reload instead of being
 * told, forever, that its link expired.
 *
 * ONE STATE LEFT THAT CONTRACT ALTOGETHER RATHER THAN JOINING IT AS A FOURTH CODE. A signer
 * reopening the link they have already signed used to get the constant sentence — a refusal
 * about a link that had just worked. It is not a refusal and it is not an error: the endpoint
 * answers 200 with `already_signed: true`, no document text and no content hash, and this file
 * needs no branch for it because it is an ordinary successful payload. `SignLink` reads the flag
 * and shows the done step, where the button that fetches the executed copy already lives.
 *
 * ── AND THE FAILURE THAT IS NOT A VERDICT ─────────────────────────────────────
 * The anti-oracle sentence is a statement ABOUT A TOKEN, and only the server can make it.
 * A `fetch` that rejects made no statement at all: the POST never reached the function —
 * a cold start that timed out, a tunnel that dropped, a phone between cells — so there is
 * no HTTP response, no attempt row, and nothing has been decided about anybody's link.
 * Telling that signer their link expired is a lie the code was inventing on the server's
 * behalf, and it is the one failure where the honest answer is "try again".
 *
 * `post` therefore classifies it: a rejection with NO response becomes a `SignLinkError`
 * with code `NETWORK` (`SIGN_LINK_NETWORK_CODE`) carrying a retryable sentence. A response
 * that ARRIVED and could not be parsed is NOT that failure — the server did answer, and it
 * keeps the constant sentence like every other refusal. `signLinkFailure` is the one place
 * that reads the difference, and `signLinkSignWithRetry` is the only thing allowed to act
 * on it automatically.
 */

import { signingError, type PassthroughCode } from './esign';

const FUNCTION_PATH = '/functions/v1/sign-agreement-public';

function endpoint(): string {
    const base = String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '');
    return `${base}${FUNCTION_PATH}`;
}

function anonKey(): string {
    return String(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '');
}

/**
 * What a link-holder's browser is allowed to see. No ids, no financials, no contacts.
 *
 * `party` and `signer_name` are what generalize this payload past the witness: the first
 * says which ceremony this link is, the second names whoever is being asked to sign — the
 * witness from the contract, or the payer name an admin froze onto the signature row for
 * somebody who has no account. Both are optional on the wire, so a link opened against a
 * deployment that predates them still resolves (see `normalizeSignLinkParty`).
 *
 * `content_sha256` is typed nullable rather than required for the same reason: it is the
 * hash of whatever `markdown` carries — frozen bytes for a witness, a live render for a
 * payer — and the one case where it is genuinely absent is an endpoint deployed before the
 * key was projected. `hashAttestation` decides what may be signed when it is.
 */
export interface SignLinkDocument {
    party?: string | null;
    /**
     * The document family. Projected, but the ceremony does NOT branch on it: a DRIVER link
     * is issuable on the deposit arc and nowhere else, so `party` already determines which
     * ceremony this is, and two sources for one decision is one too many.
     */
    doc_type?: string | null;
    document_status: string | null;
    title: string | null;
    reference_no: string | null;
    role_label: string | null;
    markdown: string | null;
    content_sha256: string | null;
    signer_name?: string | null;
    witness_name: string | null;
    driver_first_name: string | null;
    vehicle_registration: string | null;
    expires_at: string | null;
    /**
     * The ceremony behind this link is over, and this page's job is the done step rather than the
     * four before it. It comes back from a successful `token_sign` — and, since the endpoint's
     * `20260818020000` verdict, from a `token_lookup` on the signer's OWN consumed link.
     *
     * ON THAT SECOND ARM `markdown` AND `content_sha256` ARE BOTH NULL, and neither is a
     * degraded payload: there is nothing left for this holder to read or attest to, and what they
     * are entitled to now is the executed copy, which is `signLinkSignedCopy`'s business. It also
     * means `hashAttestation` answers no hash, which is what keeps a stale draft from being
     * restored on top of a finished ceremony.
     */
    already_signed: boolean;
}

export class SignLinkError extends Error {
    readonly code: string | null;
    /**
     * What actually threw, on the one path where the throw is not the server's: the raw
     * `TypeError` a dead `fetch` rejects with. Kept because the sentence shown to the signer
     * is deliberately vague and the diagnostic behind it — "Failed to fetch", a timeout, an
     * abort — is what tells us how often the edge function is cold. Sentry links it.
     */
    readonly cause: unknown;

    constructor(message: string, code: string | null = null, cause: unknown = undefined) {
        super(message);
        this.name = 'SignLinkError';
        this.code = code;
        this.cause = cause;
    }
}

/** The one sentence the endpoint returns for every rejection it will not explain. */
export const SIGN_LINK_GENERIC_MESSAGE = 'This signing link is invalid or has expired';

/**
 * The code for a request that never reached a verdict. It is the client's OWN code and the
 * endpoint may not spell it: `asJson` refuses it off the wire, so no answer from the network
 * can dress a real refusal up as a failure to reach one.
 */
export const SIGN_LINK_NETWORK_CODE = 'NETWORK';

/** What a signer is told when nothing was decided. No token, no state, no verdict. */
export const SIGN_LINK_NETWORK_MESSAGE =
    'We could not reach Primelane. Check your connection and try again.';

/** The same failure at the one place where the thing to press again is called Submit. */
export const SIGN_LINK_NETWORK_SIGN_MESSAGE =
    'We could not reach Primelane. Check your connection and press Submit again.';

/**
 * The pause before the one automatic retry. Long enough for a cold edge instance to finish
 * booting, short enough that a signer holding a phone does not decide the page is dead.
 */
export const SIGN_LINK_RETRY_DELAY_MS = 1200;

async function post(body: Record<string, unknown>): Promise<Response> {
    const key = anonKey();
    try {
        return await fetch(endpoint(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                apikey: key,
                Authorization: `Bearer ${key}`,
            },
            body: JSON.stringify(body),
        });
    } catch (error) {
        // NO RESPONSE CAME BACK, so nothing about this token was decided. The sentence names
        // the connection and nothing else — the token is not in it, and there is no state to
        // leak, because the request never got far enough to be judged.
        throw new SignLinkError(SIGN_LINK_NETWORK_MESSAGE, SIGN_LINK_NETWORK_CODE, error);
    }
}

async function asJson<T>(response: Response): Promise<T> {
    let payload: { error?: string; code?: string } & Record<string, unknown>;
    try {
        payload = (await response.json()) as typeof payload;
    } catch {
        // The server ANSWERED and we could not read it. That is a verdict we failed to
        // understand, not a request that never arrived — so it keeps the constant sentence.
        throw new SignLinkError(SIGN_LINK_GENERIC_MESSAGE);
    }
    if (!response.ok || typeof payload.error === 'string') {
        // `NETWORK` is reserved for the transport and is stripped rather than trusted: a
        // payload claiming it would otherwise buy itself a silent retry and a softer sentence.
        const code = payload.code === SIGN_LINK_NETWORK_CODE ? null : (payload.code ?? null);
        throw new SignLinkError(payload.error ?? SIGN_LINK_GENERIC_MESSAGE, code);
    }
    return payload as T;
}

export interface SignLinkFailure {
    /** True only for a request that never reached the server, so never reached a verdict. */
    network: boolean;
    /** A code the server raised AFTER accepting the token; null for everything else. */
    code: PassthroughCode | null;
    /** The sentence this failure is allowed to show. */
    message: string;
}

/**
 * The single reading of what a failed call means, and therefore of what may be said about it.
 *
 * Three outcomes and no fourth: the transport never arrived (say so, invite a retry), the
 * server raised a code the holder is entitled to (say what it means), or the server refused
 * (the constant sentence, because anything else would answer a question about a token). The
 * order matters — the transport question is asked FIRST, because a failure with no verdict
 * behind it would otherwise fall through to the sentence only a verdict may carry.
 */
export function signLinkFailure(error: unknown): SignLinkFailure {
    if (error instanceof SignLinkError && error.code === SIGN_LINK_NETWORK_CODE) {
        return { network: true, code: null, message: SIGN_LINK_NETWORK_MESSAGE };
    }
    const info = signingError(error);
    return {
        network: false,
        code: info.code,
        message: info.code ? info.message : SIGN_LINK_GENERIC_MESSAGE,
    };
}

/**
 * Opens the link. Does NOT consume the token — reading and walking away costs nothing.
 *
 * TWO SUCCESSFUL ANSWERS AND ONE REFUSAL. The document to sign, or `already_signed` for a link
 * whose own signature already stands, or the constant sentence for everything else. The second is
 * NOT an error path and must not be turned into one: it is the same 200 with the same shape, and
 * the page reads the flag. Only the server can tell those three apart, which is why they arrive
 * as data rather than as a code this file infers.
 */
export async function signLinkLookup(token: string): Promise<SignLinkDocument> {
    return await asJson<SignLinkDocument>(await post({ action: 'token_lookup', token }));
}

export interface SignLinkSignInput {
    token: string;
    /**
     * The hash of the exact bytes that were displayed: the server's own `content_sha256`
     * where the document is frozen, and the digest this browser computed where it is not.
     * `hashAttestation` decides which, and never substitutes one for the other.
     */
    contentSha256: string;
    typedName: string;
    /** `data:image/png;base64,…` straight off the pad. */
    signaturePng: string;
    consentTextVersion: string;
}

export async function signLinkSign(input: SignLinkSignInput): Promise<SignLinkDocument> {
    return await asJson<SignLinkDocument>(
        await post({
            action: 'token_sign',
            token: input.token,
            content_sha256: input.contentSha256,
            typed_name: input.typedName,
            signature_png: input.signaturePng,
            consent_text_version: input.consentTextVersion,
        }),
    );
}

/**
 * The submit the ceremony actually presses, which is `signLinkSign` plus ONE retry — and
 * only for a request that never reached a verdict.
 *
 * WHY A DUPLICATE SUBMIT IS SAFE, read off the server rather than assumed:
 *
 *   · `agreement_token_sign` takes the token row `FOR UPDATE` before it decides anything, so
 *     two attempts on one token are serialised rather than interleaved;
 *   · a token whose `consumed_at` is set is refused there — and `sign-agreement-public`
 *     re-runs `agreement_token_lookup` BEFORE it uploads anything and refuses the sign action
 *     on an `already_signed` verdict, so the second attempt does not even leave a PNG behind;
 *   · the signature row is UPDATEd by id, never inserted, and a row already `SIGNED` is
 *     refused by a second guard regardless of the token's state.
 *
 * So the worst a duplicate can do is spend one row in the per-IP ledger. It cannot produce a
 * second signature, a second consumption or an orphan object.
 *
 * WHAT THE SIGNER SEES IF THE LOST ATTEMPT ACTUALLY LANDED: the retry meets a consumed token
 * and gets the constant refusal, which stays the constant refusal here — the SUBMIT action is
 * not the place to explain a token's state. What changed on the server (20260818020000) is what
 * happens next: reopening the link no longer says it expired, it says `already_signed`, and the
 * page lands on the done step with the download. So the toast is still the constant sentence and
 * the signer is no longer stranded behind it.
 *
 * A SERVER VERDICT IS NEVER RETRIED. A refusal, a changed hash, a sign order — those are
 * answers, and asking again is how a signer gets rate-limited for the server's own no.
 */
export async function signLinkSignWithRetry(
    input: SignLinkSignInput,
    delayMs: number = SIGN_LINK_RETRY_DELAY_MS,
): Promise<SignLinkDocument> {
    try {
        return await signLinkSign(input);
    } catch (error) {
        if (!signLinkFailure(error).network) throw error;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        // Whatever the second attempt meets — a verdict or another dead connection — is what
        // the signer is told. There is no third.
        return await signLinkSign(input);
    }
}

/**
 * The executed copy. A CONSUMED token still opens this until it expires — with no email in
 * v1 that link is the only way a link-signer keeps what they signed.
 */
export async function signLinkSignedCopy(token: string): Promise<Blob> {
    const response = await post({ action: 'my_signed_copy', token });
    const contentType = response.headers.get('Content-Type') ?? '';
    if (!response.ok || !contentType.includes('application/pdf')) {
        // A refusal comes back as the constant JSON sentence; read it the normal way.
        await asJson<Record<string, unknown>>(response);
        throw new SignLinkError(SIGN_LINK_GENERIC_MESSAGE);
    }
    return await response.blob();
}

/** Reads the token out of the URL fragment, which never leaves the browser. */
export function tokenFromHash(hash: string): string | null {
    const raw = hash.replace(/^#/, '').trim();
    if (raw.length === 0) return null;
    // Accept both `#<token>` and `#token=<token>`; the admin app writes the former.
    const value = raw.startsWith('token=') ? raw.slice('token='.length) : raw;
    try {
        return decodeURIComponent(value) || null;
    } catch {
        return value || null;
    }
}
